import { Color, Flex, SkeletonText, Space, Spacing, Text, Tooltip } from '@billy/design-system'
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { FormattedDateTimeWithDelimiter } from '../../../FormattedDateTimeWithDelimiter'
import { Status } from '../../types/status'
import * as Styled from './styles'
import { shouldTruncateElement } from './utils/shouldTruncateElement'
import { statusToDotColor } from './utils/statusToDotColor'

export type TimelineStatusItemStatus = Status

export interface TimelineStatusItemProps {
  children?: ReactNode
  date?: Date | string
  dateOnly?: boolean
  description?: ReactNode
  isCollapsed?: boolean
  isLoading?: boolean
  previousItem?: boolean
  status?: Status
  title?: ReactNode
  withSeparator?: boolean
  withStatus?: boolean
}

export const TimelineStatusItem = ({
  children,
  date,
  dateOnly = false,
  description,
  isCollapsed = false,
  isLoading = false,
  status = 'neutral',
  title,
  withSeparator = true,
  withStatus = true,
}: TimelineStatusItemProps): ReactElement => {
  const descriptionTextRef = useRef<HTMLHeadingElement>(null)
  const [shouldTruncateDescriptionText, setShouldTruncateDescriptionText] = useState(false)

  const statusColor = useMemo(() => (isLoading ? Color.Grey200 : statusToDotColor[status]), [isLoading, status])

  const shouldDisplayContent = description || children || date || isLoading

  useEffect(() => {
    const descriptionTextElement = descriptionTextRef.current
    if (descriptionTextElement && shouldTruncateElement(descriptionTextElement) && description) {
      setShouldTruncateDescriptionText(true)
    }
  }, [description])

  const shouldHideDescriptionTextTooltip = !shouldTruncateDescriptionText

  return (
    <Styled.TimelineStatusItemWrapper
      isCollapsed={isCollapsed}
      statusColor={statusColor}
      withStatus={isLoading ? false : withStatus}
      withSeparator={isLoading ? false : withSeparator}
    >
      <Flex>
        {isLoading ? (
          <SkeletonText variant="bodySM" fullWidth />
        ) : (
          <>
            <Text variant="bodySM">{title}</Text>
          </>
        )}
      </Flex>

      {shouldDisplayContent && (
        <Flex flexDirection="column" gap={Spacing.S4}>
          {isLoading ? (
            <>
              <Space size="s6" />
              <SkeletonText variant="bodySM" fullWidth />
              <Space size="s4" />
              <SkeletonText variant="bodySM" width={150} />
              <Space size="s4" />
              <SkeletonText variant="bodySM" fullWidth />
            </>
          ) : (
            description && (
              <Tooltip forceHidden={shouldHideDescriptionTextTooltip} label={description}>
                <Styled.DescriptionText
                  colorVariant="secondary"
                  ref={descriptionTextRef}
                  truncate={shouldTruncateDescriptionText}
                  variant="bodySM"
                >
                  {description}
                </Styled.DescriptionText>
              </Tooltip>
            )
          )}

          {date && (
            <Text colorVariant="placeholder" truncate variant="bodySM">
              <FormattedDateTimeWithDelimiter dateOnly={dateOnly} value={date} />
            </Text>
          )}

          {children && !isCollapsed && !isLoading && <Styled.ChildrenWrapper>{children}</Styled.ChildrenWrapper>}
        </Flex>
      )}
    </Styled.TimelineStatusItemWrapper>
  )
}
